// src/components/BoardPack/pages/consolidate_boardpack/PL_Report_component.jsx
import React from "react";
import { THEME } from "../../constant/color";
import HeadingComponent from "../shared/heading";
import mawgifLogo from '@/assets/img/images.png'
import { dynamicKey } from "../../utils/dynamicKey";

const C = THEME.colors;

const LABEL_COL_PCT = 10;
const VARIANCE_COL_PCT = 7;

const BORDER_LIGHT = C.borderColor1;
const BORDER_BLACK = C.black;

const fmt = (val) => {
  if (val === null || val === undefined || val === "") return "";
  const n = Number(val);
  if (Number.isNaN(n)) return val;
  const abs = Math.abs(n).toLocaleString("en-US");
  return n < 0 ? `(${abs})` : abs;
};

const isBlockEnd = (colIndex, countryCount) => colIndex === countryCount - 1 || colIndex === countryCount * 2 - 1;
const isTotalsColumn = (i, countryCount) => i === 0 || i === countryCount;

// ✅ SAFE SPLIT: "Sep YTD 2026" → { month: "Sep YTD", year: "2026" }
//                "Sep 2026"     → { month: "Sep",     year: "2026" }
const splitPeriod = (periodStr) => {
  const parts = (periodStr || "").trim().split(" ").filter(Boolean);
  if (parts.length === 0) return { month: "", year: "" };
  const yearPart = parts[parts.length - 1];
  const monthPart = parts.slice(0, -1).join(" ");
  return { month: monthPart, year: yearPart };
};

const HeaderCell = ({ children, bg, color = C.black, isBlockEndCol = false, isFirstOfBlock = false }) => (
  <th
    style={{
      padding: "2px 1px",
      fontSize: "7.5px",
      fontWeight: 700,
      textAlign: "center",
      backgroundColor: bg,
      color,
      borderLeft: isFirstOfBlock ? `1px solid ${BORDER_BLACK}` : `1px solid ${BORDER_LIGHT}`,
      borderRight: isBlockEndCol ? `1px solid ${BORDER_BLACK}` : `1px solid ${BORDER_LIGHT}`,
      borderTop: `1px solid ${BORDER_LIGHT}`,
      borderBottom: `1px solid ${BORDER_LIGHT}`,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }}
  >
    {children}
  </th>
);

const TableHeader = ({ leftPeriodLabel, leftSubLabel, rightPeriodLabel, rightSubLabel, varianceLabel, countryColumns }) => {
  const countryCount = countryColumns.length;

  // ✅ Blocks mein month aur year alag-alag store karo
  const blocks = [
    { ...splitPeriod(leftPeriodLabel), sub: leftSubLabel },
    { ...splitPeriod(rightPeriodLabel), sub: rightSubLabel },
  ];

  const rowCell = (block, b, i, text, highlightBg, highlightColor = C.black) => {
    const flatIndex = b * countryCount + i;
    const highlighted = isTotalsColumn(flatIndex, countryCount);
    return (
      <HeaderCell
        key={`${text}-${b}-${i}`}
        bg={highlighted ? highlightBg : C.grayLight}
        color={highlighted ? highlightColor : C.black}
        isFirstOfBlock={i === 0}
        isBlockEndCol={i === countryCount - 1}
      >
        {text}
      </HeaderCell>
    );
  };

  return (
    <thead className="[&>tr:last-child>th]:border-b-2 [&>tr:last-child>th]:border-black">
      {/* Row 1: Month label (Sep / Sep YTD) */}
      <tr>
        <th
          rowSpan={3}
          style={{
            width: `${LABEL_COL_PCT}%`,
            backgroundColor: C.white,
            borderRight: `1px solid ${BORDER_BLACK}`,
            borderTop: `1px solid ${BORDER_BLACK}`,
            borderLeft: `1px solid ${BORDER_BLACK}`,
            borderBottom: `1px solid ${BORDER_LIGHT}`,
            padding: "0px",
          }}
        >
          <img
            src={mawgifLogo}
            alt="Logo"
            style={{
              width: "80%",
              height: "50px",
              objectFit: "cover",
            }}
          />
        </th>

        {blocks.map((block, b) =>
          countryColumns.map((_, i) =>
            rowCell(block, b, i, block.month, C.redBanner, C.white)
          )
        )}
        <th
          style={{
            padding: "2px 1px",
            fontSize: "7.5px",
            fontWeight: 700,
            textAlign: "center",
            backgroundColor: C.blue1,
            color: C.white,
            borderLeft: "none",
            borderRight: `1px solid ${BORDER_BLACK}`,
            borderTop: `1px solid ${BORDER_LIGHT}`,
            borderBottom: `1px solid ${BORDER_LIGHT}`,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          CURRENT MONTH
        </th>
      </tr>

      {/* Row 2: Year label (2026 / 2025) */}
      <tr>
        {blocks.map((block, b) =>
          countryColumns.map((_, i) =>
            rowCell(block, b, i, block.year, C.yellowLight, C.black)
          )
        )}
        <th
          style={{
            padding: "2px 1px",
            fontSize: "7.5px",
            fontWeight: 700,
            textAlign: "center",
            backgroundColor: C.white,
            color: C.black,
            borderLeft: "none",
            borderRight: `1px solid ${BORDER_BLACK}`,
            borderTop: `1px solid ${BORDER_LIGHT}`,
            borderBottom: `1px solid ${BORDER_LIGHT}`,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {varianceLabel}
        </th>
      </tr>

      {/* Row 3: Sub label (Actual / Budget) */}
      <tr>
        {blocks.map((block, b) =>
          countryColumns.map((_, i) => rowCell(block, b, i, block.sub, C.yellowLight, C.black))
        )}
        <th
          style={{
            padding: "2px 1px",
            fontSize: "7.5px",
            fontWeight: 700,
            textAlign: "center",
            backgroundColor: C.grayLight,
            color: C.black,
            borderLeft: "none",
            borderRight: `1px solid ${BORDER_BLACK}`,
            borderTop: `1px solid ${BORDER_LIGHT}`,
            borderBottom: `1px solid ${BORDER_LIGHT}`,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {""}
        </th>
      </tr>

      {/* Row 4: SAR'000 + Country names + Variance */}
      <tr>
        <th
          style={{
            textAlign: "left",
            padding: "1px 2px 1px 3px",
            fontSize: "7.5px",
            fontWeight: 700,
            color: C.white,
            backgroundColor: C.redBanner,
            borderLeft: `1px solid ${BORDER_BLACK}`,
            borderRight: `1px solid ${BORDER_BLACK}`,
            borderTop: `1px solid ${BORDER_LIGHT}`,
            borderBottom: `1px solid ${BORDER_BLACK}`,
          }}
        >
          SAR'000
        </th>
        {blocks.map((_, b) =>
          countryColumns.map((c, i) => (
            <HeaderCell
              key={`country-${b}-${i}`}
              bg={C.blue1}
              color={C.white}
              isFirstOfBlock={i === 0}
              isBlockEndCol={i === countryCount - 1}
            >
              {c}
            </HeaderCell>
          ))
        )}
        <HeaderCell bg={C.blue1} color={C.white} isBlockEndCol>
          Variance
        </HeaderCell>
      </tr>
    </thead>
  );
};

const getRowBorderStyle = (label) => {
  if (label === "Total Revenue") {
    return { borderTop: `1px solid ${BORDER_LIGHT}`, borderBottom: `1px solid ${BORDER_BLACK}` };
  }
  if (label === "EBITDA") {
    return { borderTop: `1px solid ${BORDER_LIGHT}`, borderBottom: `1px solid ${BORDER_LIGHT}` };
  }
  if (label === "Zakat, Exceptional & CG") {
    return { borderTop: `1px solid ${BORDER_LIGHT}`, borderBottom: `1px solid ${BORDER_BLACK}` };
  }
  if (label === "OPERATING PROFIT/(LOSS)") {
    return { borderTop: `1px solid ${BORDER_BLACK}`, borderBottom: `1px solid ${BORDER_LIGHT}` };
  }
  if (label === "NET PROFIT MARGIN" || label === "NET PROFIT") {
    return { borderTop: `1px solid ${BORDER_LIGHT}`, borderBottom: `1px solid ${BORDER_BLACK}` };
  }
  return { borderTop: `1px solid ${BORDER_LIGHT}`, borderBottom: `1px solid ${BORDER_LIGHT}` };
};

const BOLD_LABELS = ["EBITDA", "EBITDA Margin", "OPERATING PROFIT/(LOSS)", "NET PROFIT MARGIN", "Total Revenue"];
const SUBTOTAL_SILVER_LABELS = ["EBITDA", "OPERATING PROFIT/(LOSS)"];
const MARGIN_YELLOW_LABELS = ["EBITDA Margin", "NET PROFIT MARGIN"];

const BodyRow = ({ row, countryCount, dataColCount }) => {
  if (row.kind === "sectionHeader") {
    return (
      <tr>
        <td
          style={{
            padding: "1px 2px",
            fontSize: "7.5px",
            fontWeight: 700,
            color: C.white,
            backgroundColor: C.redBanner,
            borderLeft: `1px solid ${BORDER_BLACK}`,
            borderRight: `1px solid ${BORDER_BLACK}`,
            borderTop: `1px solid ${BORDER_BLACK}`,
            borderBottom: `1px solid ${BORDER_BLACK}`,
          }}
        >
          {row.label}
        </td>
        {row.values.map((v, i) => (
          <td
            key={i}
            style={{
              padding: "2px 1px",
              fontSize: "7.5px",
              fontWeight: 700,
              textAlign: "center",
              color: C.white,
              backgroundColor: C.blue1,
              borderLeft: `1px solid ${BORDER_LIGHT}`,
              borderRight: isBlockEnd(i, countryCount) ? `1px solid ${BORDER_BLACK}` : `1px solid ${BORDER_LIGHT}`,
              borderTop: `1px solid ${BORDER_BLACK}`,
              borderBottom: `1px solid ${BORDER_BLACK}`,
            }}
          >
            {v}
          </td>
        ))}
      </tr>
    );
  }

  const bold = BOLD_LABELS.includes(row.label);
  const isSilverRow = SUBTOTAL_SILVER_LABELS.includes(row.label);
  const isMarginRow = MARGIN_YELLOW_LABELS.includes(row.label);
  const isPctRow = !!row.noVariance;
  const rowBorder = getRowBorderStyle(row.label);

  const labelBg = isSilverRow
    ? C.silver
    : isMarginRow || isPctRow
    ? C.yellowBanner
    : C.grayLight;

  const cellBg = (colIndex) => {
    const isTotalsCol = colIndex === 0 || colIndex === countryCount;
    if (isSilverRow) return C.silver;
    if (isMarginRow) return isTotalsCol ? C.yellowLight : C.yellowBanner;
    if (isPctRow) return isTotalsCol ? C.yellowLight : C.white;
    return isTotalsCol ? C.yellowLight : C.white;
  };

  const varianceBg = isSilverRow ? C.silver : C.yellowLight;

  return (
    <tr>
      <td
        style={{
          fontSize: "7.5px",
          fontWeight: bold ? 700 : 400,
          color: C.black,
          padding: "1px 2px 1px 3px",
          borderLeft: `1px solid ${BORDER_BLACK}`,
          borderRight: `1px solid ${BORDER_BLACK}`,
          ...rowBorder,
          backgroundColor: labelBg,
          whiteSpace: "normal",
          wordBreak: "break-word",
        }}
      >
        {row.label}
      </td>

      {row.values.slice(0, dataColCount).map((value, colIndex) => {
        const isNumber = typeof value === "number";
        const isNegative = isNumber ? value < 0 : typeof value === "string" && value.trim().startsWith("(");
        return (
          <td
            key={colIndex}
            style={{
              textAlign: "center",
              padding: "1px 2px",
              fontSize: "7.5px",
              fontWeight: bold ? 700 : 400,
              color: isNegative ? C.red : C.black,
              backgroundColor: cellBg(colIndex),
              borderLeft: `1px solid ${BORDER_LIGHT}`,
              borderRight: isBlockEnd(colIndex, countryCount) ? `1px solid ${BORDER_BLACK}` : `1px solid ${BORDER_LIGHT}`,
              ...rowBorder,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {isNumber ? fmt(value) : value || (value === 0 ? "0" : "-")}
          </td>
        );
      })}

      {(() => {
        const varianceValue = row.values[dataColCount];
        const isVarianceNegative =
          typeof varianceValue === "number"
            ? varianceValue < 0
            : typeof varianceValue === "string" && varianceValue.trim().startsWith("(");
        const varianceColor = isVarianceNegative ? C.red : C.black;

        if (row.skipVarianceCell) return null;

        if (row.varianceRowSpan) {
          return (
            <td
              rowSpan={row.varianceRowSpan}
              style={{
                padding: 0,
                verticalAlign: "top",
                borderLeft: "none",
                borderRight: "none",
                borderTop: "none",
                borderBottom: "none",
                backgroundColor: C.white,
              }}
            >
              <div
                style={{
                  backgroundColor: varianceBg,
                  textAlign: "center",
                  padding: "1px 2px",
                  fontSize: "7.5px",
                  fontWeight: bold ? 700 : 400,
                  color: varianceColor,
                  borderLeft: `1px solid ${BORDER_LIGHT}`,
                  borderRight: `1px solid ${BORDER_BLACK}`,
                  borderTop: rowBorder.borderTop,
                  borderBottom: rowBorder.borderBottom,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {varianceValue ?? ""}
              </div>
            </td>
          );
        }

        return (
          <td
            style={{
              textAlign: "center",
              padding: "1px 2px",
              fontSize: "7.5px",
              fontWeight: bold ? 700 : 400,
              color: varianceColor,
              backgroundColor: varianceBg,
              borderLeft: `1px solid ${BORDER_LIGHT}`,
              borderRight: `1px solid ${BORDER_BLACK}`,
              ...rowBorder,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {varianceValue ?? ""}
          </td>
        );
      })()}
    </tr>
  );
};

const attachVarianceSpan = (rows) => {
  const sectionIdx = rows.findIndex((r) => r.kind === "sectionHeader");
  if (sectionIdx === -1) return rows;
  const anchorIdx = sectionIdx - 1;
  if (anchorIdx < 0) return rows;
  const span = rows.length - anchorIdx;
  return rows.map((row, i) => {
    if (i === anchorIdx) return { ...row, varianceRowSpan: span };
    if (i > anchorIdx) return { ...row, skipVarianceCell: true };
    return row;
  });
};

const PLTable = ({ headerProps, rows, countryColumns }) => {
  const preparedRows = attachVarianceSpan(rows);
  const countryCount = countryColumns.length;
  const dataColCount = countryCount * 2;
  const dataColPct = (100 - LABEL_COL_PCT - VARIANCE_COL_PCT) / dataColCount;
  return (
    <div
      style={{
        position: "relative",
        borderLeft: `1px solid ${BORDER_BLACK}`,
        borderTop: `1px solid ${BORDER_BLACK}`,
        borderBottom: "none",
        borderRight: "none",
        backgroundColor: C.white,
        width: "100%",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          left: 0,
          bottom: 0,
          width: `${100 - VARIANCE_COL_PCT}%`,
          borderBottom: `1px solid ${BORDER_BLACK}`,
        }}
      />
      <table
        style={{
          width: "100%",
          tableLayout: "fixed",
          borderCollapse: "collapse",
        }}
      >
        <colgroup>
          <col style={{ width: `${LABEL_COL_PCT}%` }} />
          {Array.from({ length: dataColCount }).map((_, i) => (
            <col key={i} style={{ width: `${dataColPct}%` }} />
          ))}
          <col style={{ width: `${VARIANCE_COL_PCT}%` }} />
        </colgroup>
        <TableHeader {...headerProps} countryColumns={countryColumns} />
        <tbody>
          {preparedRows.map((row, idx) => (
            <BodyRow key={idx} row={row} countryCount={countryCount} dataColCount={dataColCount} />
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const PLActualVsPYTable = ({ data }) => {
  if (!data?.rows?.length || !data?.countryColumns?.length) return null;
  return (
    <PLTable
      headerProps={{
        leftPeriodLabel: data.leftPeriodLabel,
        leftSubLabel: "Actual",
        rightPeriodLabel: data.rightPeriodLabel,
        rightSubLabel: "Actual",
        varianceLabel: data.varianceLabel,
      }}
      rows={data.rows}
      countryColumns={data.countryColumns}
    />
  );
};

export const PLActualVsBudgetTable = ({ data }) => {
  if (!data?.rows?.length || !data?.countryColumns?.length) return null;
  return (
    <PLTable
      headerProps={{
        leftPeriodLabel: data.leftPeriodLabel,
        leftSubLabel: "Actual",
        rightPeriodLabel: data.rightPeriodLabel,
        rightSubLabel: "Budget",
        varianceLabel: data.varianceLabel,
      }}
      rows={data.rows}
      countryColumns={data.countryColumns}
    />
  );
};

const PL_Report_component = ({
  title = "",
  pyData,
  budgetData,
  contract = "",
  year = "",
  month = "",
}) => {
  return (
    <div className="w-full bg-white px-2 flex flex-col gap-3">
      <div className="flex justify-center items-center relative mt-3">
        <HeadingComponent variant="main">{dynamicKey({ text: title, year, month, contract })}</HeadingComponent>
        <div className="font-md text-[18px] absolute right-6 top-4">CFO Report</div>
      </div>

      <PLActualVsPYTable data={pyData} />
      <PLActualVsBudgetTable data={budgetData} />
    </div>
  );
};

export default PL_Report_component;